class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;

      if (this.hideAnnouncementHint()) return;
      this.close();
    });
    this.addEventListener('click', this.handleAnnouncementClick.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.handleCartItemsScroll = this.handleCartItemsScroll.bind(this);
    this.initializeOrderSummaryScroll();
  }

  handleAnnouncementClick(event) {
    const trigger = event.target.closest('[data-cart-drawer-announcement-trigger]');
    if (!trigger) return;

    const hint = this.querySelector('[data-cart-drawer-announcement-hint]');
    if (!hint) return;

    window.clearTimeout(this.announcementHintTimer);
    hint.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.announcementHintTimer = window.setTimeout(() => this.hideAnnouncementHint(), 3500);
  }

  hideAnnouncementHint() {
    const hint = this.querySelector('[data-cart-drawer-announcement-hint]');
    if (!hint || hint.hidden) return false;

    hint.hidden = true;
    this.querySelector('[data-cart-drawer-announcement-trigger]')?.setAttribute('aria-expanded', 'false');
    window.clearTimeout(this.announcementHintTimer);
    return true;
  }

  initializeOrderSummaryScroll() {
    const cartItems = this.querySelector('cart-drawer-items');
    if (cartItems === this.cartItemsScrollTarget) return;

    this.cartItemsScrollTarget?.removeEventListener('scroll', this.handleCartItemsScroll);
    this.cartItemsScrollTarget = cartItems;
    cartItems?.addEventListener('scroll', this.handleCartItemsScroll, { passive: true });
  }

  handleCartItemsScroll() {
    const cartItems = this.cartItemsScrollTarget;
    if (!cartItems || cartItems.scrollHeight <= cartItems.clientHeight) return;

    const hasReachedBottom = cartItems.scrollTop + cartItems.clientHeight >= cartItems.scrollHeight - 1;
    if (!hasReachedBottom) return;

    const orderSummary = this.querySelector('.cart-drawer__order-summary');
    if (orderSummary && !orderSummary.open) orderSummary.open = true;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.initializeOrderSummaryScroll();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.initializeOrderSummaryScroll();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
